import React from 'react';
import FormInput from '../../../Components/Ui/FormInput';
import FormSelect from '../../../Components/FormFields/FormSelect';
import FormDate from '../../../Components/FormFields/FormDate';
import useTailwindBrand from '../../../Theme/useTailwindBrand';

export interface IdentityValues {
    firstName: string;
    lastName: string;
    /** 'MAN' | 'WOMAN' | '' */
    gender: string;
    phone?: string;
    email?: string;
}

interface IdentityFieldsProps {
    values: IdentityValues;
    errors: Record<string, string | undefined>;
    onFieldChange: (name: 'firstName' | 'lastName' | 'phone' | 'email', value: string) => void;
    onGenderChange: (gender: 'MAN' | 'WOMAN') => void;
    onBirthdayChange: (date: any) => void;
    birthdayDefault?: any;
    showPhone?: boolean;
    showEmail?: boolean;
}

/**
 * Bloc d'identité commun à l'inscription prof et client (étape 2) :
 * prénom, nom, genre, date de naissance, puis téléphone ou e-mail selon le
 * mode de connexion. Chaque parent garde sa propre validation et ses champs
 * spécifiques (matières et parrainage côté prof, élève et notifications côté
 * client).
 */
const IdentityFields = ({
    values,
    errors,
    onFieldChange,
    onGenderChange,
    onBirthdayChange,
    birthdayDefault,
    showPhone = false,
    showEmail = false,
}: IdentityFieldsProps) => {
    const { classes } = useTailwindBrand();

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        onFieldChange(e.target.name as 'firstName' | 'lastName' | 'phone' | 'email', e.target.value);
    };

    return (
        <>
            {['firstname', 'lastname'].map((field, index) => (
                <div key={index} className="w-full">
                    <FormInput
                        label={field === 'firstname' ? 'Prénom' : 'Nom'}
                        name={field === 'firstname' ? 'firstName' : 'lastName'}
                        type="text"
                        placeholder={field === 'firstname' ? 'Ex : John' : 'Ex : Doe'}
                        value={field === 'firstname' ? values.firstName || '' : values.lastName || ''}
                        error={field === 'firstname' ? errors.firstName : errors.lastName}
                        onChange={handleInput}
                        required
                    />
                </div>
            ))}
            <div className="w-full">
                <FormSelect
                    callback={(value: number) => {
                        onGenderChange(value === 1 ? 'WOMAN' : 'MAN');
                    }}
                    required={true}
                    error={errors.gender}
                    label="Vous êtes"
                    placeholder="Ex: Une femmme"
                    data={['Une femme', 'Un homme']}
                    defaultValue={values.gender === 'WOMAN' ? 1 : values.gender === 'MAN' ? 2 : ''}
                />
            </div>
            <div className="w-full">
                <FormDate
                    callback={onBirthdayChange}
                    required={true}
                    error={errors.birthday}
                    defaultValue={birthdayDefault}
                    className={`/border rounded-md ${errors.birthday ? `${classes.error} ${classes.errorBorder} border-l-4 border-t border-r` : 'hover:border-2 hover:border-gray-400'} `}
                />
            </div>
            {showPhone && (
                <div className="w-full">
                    <FormInput
                        label="Numéro de téléphone"
                        name="phone"
                        type="tel"
                        placeholder="06 12 34 56 78"
                        value={values.phone || ''}
                        error={errors.phone}
                        onChange={handleInput}
                        required
                        autoComplete="tel"
                    />
                </div>
            )}
            {showEmail && (
                <div className="w-full">
                    <FormInput
                        label="E-mail"
                        name="email"
                        type="email"
                        placeholder="john.dupont@email.com"
                        value={values.email || ''}
                        error={errors.email}
                        onChange={handleInput}
                        required
                        autoComplete="email"
                    />
                </div>
            )}
        </>
    );
};

export default IdentityFields;
